/**
 * Server-side PDF download helper.
 *
 * Calls POST /api/marketing/render-pdf which uses headless Chromium on the
 * backend to render a public brochure route as A4 PDF, respecting native
 * CSS @page, page-break rules, fonts and gradients. No more html2canvas
 * artefacts, no overlapping content, no toolbar bleed.
 */

import { BACKEND_URL } from './api';

/**
 * Triggers a server-side PDF render of a whitelisted public brochure path
 * and downloads the resulting file in the user's browser.
 *
 * @param {string} path - Public route to render (e.g. "/brochure/sales")
 * @param {string} fallbackFilename - Used if the server didn't supply a Content-Disposition filename
 */
export async function downloadServerPDF(path, fallbackFilename = 'document.pdf', extra = {}) {
  const res = await fetch(`${BACKEND_URL}/api/marketing/render-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, ...extra }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`PDF render failed (${res.status}): ${text || res.statusText}`);
  }

  // Pull filename out of Content-Disposition if present
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  const filename = match ? match[1] : fallbackFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
